import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, client } from '@/api/client';
import { useAuthStore } from './auth.store';
import type {
  Chat,
  ChatHistoryPage,
  ChatMessage,
  ChatReferenceSuggestion,
  ChatTypingSignal,
  ChatUnreadSummary,
  SubjectChatState,
} from '@/types/chat';
import type { Page } from '@/types';

/** One open conversation's loaded state. */
interface ChatThread {
  messages: ChatMessage[];
  /** More history exists above the oldest message held. */
  hasMore: boolean;
  loading: boolean;
  /** True once a history request has come back, whatever it contained. */
  loaded: boolean;
  /** userId → display name, for the "…is typing" line. */
  typing: Record<string, string>;
}

function emptyThread(): ChatThread {
  return { messages: [], hasMore: false, loading: false, loaded: false, typing: {} };
}

/**
 * Shared placeholder for a chat nothing has loaded yet.
 *
 * Frozen, and never written to, so that reading a thread can stay a pure
 * lookup — see `threadFor`.
 */
const ABSENT_THREAD: ChatThread = Object.freeze(emptyThread());

/**
 * Chats, kept live by the realtime channel.
 *
 * The store exists because the same conversation is rendered in two places —
 * embedded on a card and again on the chat page — and both must show the same
 * thing at the same moment. Threads are therefore cached by chat id rather than
 * held inside a component, and one socket subscription per chat serves however
 * many panels happen to be mounted on it. That is also what makes navigating
 * away and back instant instead of a reload.
 *
 * The socket is the fast path, not the only path. Every mutation still goes
 * through REST and applies the server's answer, so a dropped frame costs a
 * refresh rather than a wrong screen.
 */
export const useChatStore = defineStore('chat', () => {
  const auth = useAuthStore();

  const chats = ref<Chat[]>([]);
  /**
   * Threads by chat id, as a plain object rather than a `Map`.
   *
   * A `Map` in a `ref` was subtly wrong here and cost a stuck spinner. Vue
   * hands back a *reactive proxy* from `map.get()`, but the code that created
   * an entry held the *raw* object it had just constructed — so the very first
   * load of a chat mutated the raw thread, nothing was tracked, and the panel
   * that had already rendered `loading: true` never learned it had finished.
   * Posting a message happened to fix it, because by then `get()` returned the
   * proxy. A plain object has no such split: every read below goes through
   * `threads.value`, so every write is tracked.
   */
  const threads = ref<Record<string, ChatThread>>({});
  const summary = ref<ChatUnreadSummary>({ chats: 0, messages: 0, mentions: 0 });
  const listLoading = ref(false);

  /** Subscription refcount per chat: two panels, one room. */
  const openCounts = new Map<string, number>();
  let listening = false;

  const unreadTotal = computed(() => summary.value.chats);

  /**
   * A thread for reading — a pure lookup, safe to call from a computed.
   *
   * Deliberately does not create anything. The previous version did, which put
   * a write to a reactive dependency inside every component's `computed`, and
   * that is a loop waiting to happen quite apart from the proxy bug above.
   */
  function threadFor(chatId: string): ChatThread {
    return threads.value[chatId] ?? ABSENT_THREAD;
  }

  /** A thread for writing. Reads the entry back so the caller holds the proxy. */
  function ensureThread(chatId: string): ChatThread {
    if (!threads.value[chatId]) threads.value[chatId] = emptyThread();
    return threads.value[chatId];
  }

  function upsertChat(chat: Chat): void {
    const index = chats.value.findIndex((entry) => entry.id === chat.id);
    if (index >= 0) chats.value[index] = chat;
    else chats.value = [chat, ...chats.value];
  }

  function find(chatId: string): Chat | undefined {
    return chats.value.find((entry) => entry.id === chatId);
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  async function loadList(options: { companyId?: string; projectId?: string; unreadOnly?: boolean; search?: string } = {}): Promise<void> {
    listLoading.value = true;
    try {
      const response = await api.get<Page<Chat>>('chats', {
        companyId: options.companyId,
        projectId: options.projectId,
        unreadOnly: options.unreadOnly ? 'true' : undefined,
        search: options.search || undefined,
        limit: 100,
      });
      chats.value = response.items;
    } finally {
      listLoading.value = false;
    }
  }

  async function refreshSummary(companyId?: string | null): Promise<void> {
    try {
      summary.value = await api.get<ChatUnreadSummary>('chats/unread', { companyId: companyId ?? undefined });
    } catch {
      // The badge is not worth an error toast: the plugin may simply be off
      // here, which is not a failure the reader can act on.
      summary.value = { chats: 0, messages: 0, mentions: 0 };
    }
  }

  /**
   * The chat on a subject, or null when nobody has enabled one.
   *
   * The server wraps the answer in an object on purpose — see the route's own
   * comment. An unwrapped `null` arrives here as a truthy empty `Blob`, which
   * is indistinguishable from a real chat until something tries to read its id.
   */
  async function forSubject(subjectType: string, subjectId: string): Promise<SubjectChatState> {
    const response = await api.get<SubjectChatState>(`chats/subjects/${subjectType}/${subjectId}`);
    const state: SubjectChatState = {
      chat: response?.chat ?? null,
      canEnable: response?.canEnable ?? false,
      autoEnable: response?.autoEnable ?? false,
    };
    if (state.chat) upsertChat(state.chat);
    return state;
  }

  async function enable(subjectType: string, subjectId: string): Promise<Chat> {
    const chat = await api.post<Chat>(`chats/subjects/${subjectType}/${subjectId}`, {});
    upsertChat(chat);
    return chat;
  }

  /** The newest page of history. Safe to call again; it replaces what it holds. */
  async function loadHistory(chatId: string): Promise<void> {
    const thread = ensureThread(chatId);
    thread.loading = true;
    try {
      const page = await api.get<ChatHistoryPage>(`chats/${chatId}/messages`, { limit: 50 });
      thread.messages = page.items ?? [];
      thread.hasMore = page.hasMore ?? false;
    } finally {
      // Set whatever happened: a failed load must still stop the spinner, or
      // the panel sits on it forever with nothing to say.
      thread.loading = false;
      thread.loaded = true;
    }
  }

  /** Older messages, prepended. Called when the reader scrolls to the top. */
  async function loadOlder(chatId: string): Promise<void> {
    const thread = ensureThread(chatId);
    const oldest = thread.messages[0];
    if (!oldest || thread.loading || !thread.hasMore) return;

    thread.loading = true;
    try {
      const page = await api.get<ChatHistoryPage>(`chats/${chatId}/messages`, { before: oldest.id, limit: 50 });
      thread.messages = [...(page.items ?? []), ...thread.messages];
      thread.hasMore = page.hasMore ?? false;
    } finally {
      thread.loading = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Writing
  // ---------------------------------------------------------------------------

  async function post(chatId: string, body: string, attachmentIds: string[] = []): Promise<void> {
    const message = await api.post<ChatMessage>(`chats/${chatId}/messages`, { body, attachmentIds });
    // The socket will also deliver this one; `applyMessage` de-duplicates, and
    // applying it here means the composer clears against a real id rather than
    // waiting on a round trip through the gateway.
    applyMessage(message);
  }

  async function edit(chatId: string, messageId: string, body: string): Promise<void> {
    const message = await api.patch<ChatMessage>(`chats/${chatId}/messages/${messageId}`, { body });
    applyMessage(message);
  }

  async function remove(chatId: string, messageId: string): Promise<void> {
    await api.delete(`chats/${chatId}/messages/${messageId}`);
    applyDeletion(chatId, messageId);
  }

  /** Rows for the composer's  picker, scoped to the chat by the server. */
  async function searchReferences(chatId: string, query: string): Promise<ChatReferenceSuggestion[]> {
    // An empty query is allowed and answers with recent items, so pressing `#`
    // opens a populated picker rather than nothing at all.
    const rows = await api.get<ChatReferenceSuggestion[]>(`chats/${chatId}/references`, { q: query.trim() });
    return rows ?? [];
  }

  async function markRead(chatId: string, messageId?: string): Promise<void> {
    const chat = await api.post<Chat>(`chats/${chatId}/read`, { messageId });
    upsertChat(chat);
    await refreshSummary(chat.companyId);
  }

  async function setMuted(chatId: string, muted: boolean): Promise<void> {
    const chat = await api.post<Chat>(`chats/${chatId}/mute`, { muted });
    upsertChat(chat);
    await refreshSummary(chat.companyId);
  }

  // ---------------------------------------------------------------------------
  // Live updates
  // ---------------------------------------------------------------------------

  function applyMessage(message: ChatMessage): void {
    const thread = ensureThread(message.chatId);
    const index = thread.messages.findIndex((entry) => entry.id === message.id);
    if (index >= 0) thread.messages[index] = message;
    else thread.messages = [...thread.messages, message];

    const chat = find(message.chatId);
    if (chat && !message.deletedAt) {
      chat.lastMessageAt = message.createdAt;
      chat.lastMessagePreview = message.body.slice(0, 280);
      chat.lastMessageAuthor = message.author;
    }
  }

  function applyDeletion(chatId: string, messageId: string): void {
    const thread = threads.value[chatId];
    if (!thread) return;
    // The tombstone stays in place — see `ChatMessage` on the server. Removing
    // the row under a reader is what renumbers everything below their eyes.
    thread.messages = thread.messages.map((entry) =>
      entry.id === messageId ? { ...entry, body: '', bodyHtml: null, deletedAt: new Date().toISOString() } : entry,
    );
  }

  function applyTyping(payload: ChatTypingSignal): void {
    if (!payload.chatId) return;
    const thread = ensureThread(payload.chatId);
    if (payload.typing) thread.typing[payload.userId] = payload.displayName;
    else delete thread.typing[payload.userId];
  }

  /**
   * Wires the socket. Called once after sign-in, like the notification store.
   *
   * Note that this listens whether or not a panel is open: an unread badge that
   * only updated while you were already looking at the chat would be pointless.
   */
  function listen(): void {
    if (listening) return;
    listening = true;

    client.realtime.on<{ chatId: string; message: ChatMessage }>('chat:message', (payload) => {
      applyMessage(payload.message);
      const chat = find(payload.chatId);
      // Somebody else's message in a chat nobody has open here is what the
      // badge is for. Our own is never unread — including the copy that comes
      // back when we posted it from another tab, which has no open panel here
      // to suppress it.
      const mine = payload.message.author?.id === auth.user?.id;
      if (chat && !mine && !openCounts.get(payload.chatId)) {
        chat.unreadCount += 1;
        if (!chat.muted) summary.value = { ...summary.value, messages: summary.value.messages + 1 };
      }
    });

    client.realtime.on<{ chatId: string; message: ChatMessage }>('chat:message-updated', (payload) => applyMessage(payload.message));
    client.realtime.on<{ chatId: string; messageId: string }>('chat:message-deleted', (payload) =>
      applyDeletion(payload.chatId, payload.messageId),
    );
    client.realtime.on<ChatTypingSignal>('typing', applyTyping);
  }

  /**
   * Joins a chat's room, refcounted.
   *
   * Two panels on the same conversation — the card and the chat page side by
   * side — must not have the first one to unmount take the second one's live
   * updates with it.
   */
  function open(chatId: string): void {
    const count = openCounts.get(chatId) ?? 0;
    openCounts.set(chatId, count + 1);
    if (count === 0) client.realtime.subscribe([`chat:${chatId}`]);
  }

  function close(chatId: string): void {
    const count = openCounts.get(chatId) ?? 0;
    if (count <= 1) {
      openCounts.delete(chatId);
      client.realtime.unsubscribe([`chat:${chatId}`]);
      const thread = threads.value[chatId];
      if (thread) thread.typing = {};
    } else {
      openCounts.set(chatId, count - 1);
    }
  }

  function setTyping(chatId: string, typing: boolean): void {
    client.realtime.setTypingIn(`chat:${chatId}`, typing);
  }

  return {
    chats,
    threads,
    summary,
    listLoading,
    unreadTotal,
    threadFor,
    find,
    loadList,
    refreshSummary,
    forSubject,
    enable,
    loadHistory,
    loadOlder,
    post,
    edit,
    remove,
    searchReferences,
    markRead,
    setMuted,
    listen,
    open,
    close,
    setTyping,
  };
});
